import { getUserResources } from '@hotjar/user-resources';
import { getCurrentUser } from '@hotjar/user';
import type { CostSpecDTO } from '@hotjar/webapp/domains/billing';
import type { Settings } from '@hotjar/webapp/domains/common/settings';
import { loadWindowSettings } from '@hotjar/webapp/utils/load-window-settings';
import { PricingRepository } from '@hotjar/webapp/domains/revenue/pricing/pricing-repository';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';
import type { OwnedAccount } from '@hotjar/account';
import { countBy } from 'lodash-es';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';

export type PlanChanges = Array<'upgrade' | 'downgrade' | 'change'>;
interface PlanChangeInfo {
  list: PlanChanges;
  upgradeCount: number;
  downgradeCount: number;
  downgradeHighestPlan?: FeatureGroup;
}

export const getPlanChanges = async (
  account: OwnedAccount,
  costSpecs: CostSpecDTO[],
): Promise<PlanChangeInfo> => {
  try {
    const [user, settings] = await Promise.all([getCurrentUser(), loadWindowSettings()]);
    if (settings instanceof Error || !user) {
      return { list: [], upgradeCount: 0, downgradeCount: 0 };
    }

    const { sites } = await getUserResources(+user.id);

    const pricingRepository = new PricingRepository((settings as Settings).pricing_tables);
    const pricingTable = pricingRepository.byVersion(account.pricingTableId);

    const planGt = (lhs: string, rhs: string) => pricingTable[lhs].gt(pricingTable[rhs]);
    const planLt = (lhs: string, rhs: string) => pricingTable[lhs].lt(pricingTable[rhs]);

    const result = sites
      .filter((site) => {
        const updatedPlan = costSpecs.find((costSpec) => costSpec.site_id === site.id);

        const isAccountSite = site.account.id === account.id;
        const planChanged = site.planId !== updatedPlan?.plan_id;
        return updatedPlan !== undefined && isAccountSite && planChanged;
      })
      .map((site) => {
        const updatedPlan = costSpecs.find((costSpec) => costSpec.site_id === site.id);
        const isUpgrading = planGt(updatedPlan?.plan_id as string, site.planId);
        return isUpgrading ? 'upgrade' : 'downgrade';
      });

    const downgrades = sites.filter((site) => {
      const updatedPlan = costSpecs.find((costSpec) => costSpec.site_id === site.id);
      return updatedPlan !== undefined && planLt(updatedPlan?.plan_id ?? '', site.planId);
    });

    const highestDowngradeTier = Math.max(
      ...downgrades.map((site) => {
        const featureGroup = site.plan;
        return settings.plans[featureGroup]?.tier;
      }),
    );

    const downgradeHighestPlan = Object.keys(settings.plans).find((featureGroup) => {
      const { tier } = settings.plans[featureGroup as FeatureGroup];
      return tier === highestDowngradeTier;
    }) as FeatureGroup;

    return {
      list: result,
      upgradeCount: countBy(result)?.upgrade ?? 0,
      downgradeCount: countBy(result)?.downgrade ?? 0,
      downgradeHighestPlan,
    };
  } catch {
    return { list: [], upgradeCount: 0, downgradeCount: 0 };
  }
};

export async function getMultipleSkuPlanChanges(costSpecs: CostSpecDTO[]): Promise<PlanChangeInfo> {
  return { list: costSpecs.map(() => 'change'), upgradeCount: 0, downgradeCount: 0 };
}

export const triggerPlanChanges = (planChanges: PlanChanges): void => {
  planChanges.forEach((change) => {
    event(`plan_${change}`);
  });
};
